import { Amount, AmountJSON } from './Amount';
import { Account } from './Account';
import { Counterparty } from "./Counterparty";
import { areNumbersEqual, assertType, toDateStr, uuid } from "../base/utils";
import { DiffContent, Entity, Mutation, TrackingBuilder } from "../base";
import { Ref } from './Ref';
import { TransactionType } from '.';
import { ExternalFile } from './ExternalFile';

class Entry {
    private _uuid: string;
    private _account: Ref<Account>;
    private _amount: Amount;
    private _counterparty: Counterparty | undefined;
    constructor(uuid : string, account:Ref<Account>, amount:Amount) {
        assertType(account, "account", Ref);
        assertType(amount, "amount", Amount);
        assertType(uuid, "uuid", String);

        this._uuid = uuid;
        this._account = account;
        this._amount = amount;
        this._counterparty = undefined;
    }

    get uuid() { return this._uuid; }
    get account () { return this._account; }
    get amount () { return this._amount; }
    get counterparty () { return this._counterparty; }

    string() {
        return this._account.obj().fullName() + "\t" + this._amount.string();
    }

    toJson() : EntryJSON {
        return {
            uuid: this._uuid,
            account : this._account.uuid,
            amount: this._amount.toJson()
        }
    }

    static fromJson(entry : EntryJSON) : Entry  {
        const account = Account.createRef(entry.account);
        const amount = Amount.fromJson(entry.amount);
        return new Entry(entry.uuid, account, amount);
    }

    static areBalanced(entries:Entry[]) {
        assertType(entries, "entries", Array, Entry);
        const sum = entries
            .map(ent => ent.amount.value)
            .reduce((a, b) => a + b, 0);
        return areNumbersEqual(Math.round(sum), 0);
    }
}
class TransactionBrief {
    private _uuid: string;
    private _date: Date;
    private _counterparty: Ref<Counterparty>;
    private _amount: Amount;
    
    constructor(uuid : string, date:Date, counterparty:Ref<Counterparty>, amount:Amount){
        this._uuid = uuid;
        this._date = date;
        this._counterparty = counterparty;
        this._amount = amount;
    }
    
    get uuid () { return this._uuid; }
    get date () { return this._date; }
    get counterparty () { return this._counterparty.obj().name; }
    get amount () { return this._amount; }
}

class Transaction implements Entity<Transaction> {
    static STORE = "Transaction";

    private _uuid: string;
    private _date: Date;
    private _counterparty: Ref<Counterparty>;
    private _entries: Entry[];
    private _transactionType: Ref<TransactionType> | undefined = undefined;
    private _description: string | undefined = undefined;
    private _attachments:Ref<ExternalFile>[] = [];

    constructor(json:TransactionObj) {
        this._uuid = json.uuid;
        this._date = json.date;
        this._counterparty = json.counterparty;
        this._description = json.description;
        this._entries = json.entries;
        this._transactionType = json.transactionType;
        this._attachments = json.attachments;
    }

    validate() {
        assertType(this._uuid, "uuid", String);
        assertType(this._date, "date", Date);
        assertType(this._counterparty, "counterparty", Ref);
        assertType(this._entries, "entries", Array, Entry);

        if (this._transactionType) {
            assertType(this._transactionType, "transactionType", Ref);
        }

        if (!Entry.areBalanced(this._entries)) {
            throw Error("Entries not balanced");
        }
    }

    get uuid () { return this._uuid; }
    get date () { return this._date; }
    get counterparty () { return this._counterparty; }
    get entries () { return this._entries; }
    get transactionType() { return this._transactionType; }
    get description() { return this._description; }
    get attachments() { return this._attachments; }
    
    string() {
        const entriesStr = this._entries
            .map(entry => "\t" + entry.string() + "\n")
            .reduce((a,b) => a + b);
        return toDateStr(this._date) + "\t" + this._counterparty.obj().name + "\n" + entriesStr;
    }
    
    toJson() : TransactionJSON {
        return {
            uuid: this._uuid,
            date: this._date.toISOString(),
            counterparty: this._counterparty.uuid,
            transactionType: this._transactionType ? this._transactionType.uuid : undefined,
            description: this._description,
            entries: this._entries.map(e => e.toJson()),
            attachments: this._attachments.map(att => att.uuid),
        }
    }

    getBrief() : TransactionBrief {
        const amount = this._entries
            .map(ent => ent.amount)
            .filter(amount => amount.value > 0)
            .reduce(Amount.add);
        return new TransactionBrief(this._uuid, this._date, this._counterparty, amount);
    }

    builder() : TransactionBuilder {
        return new TransactionBuilder(this);
    }

    findReplaceAccount(find: Account, replace:Account) : Transaction {
        const toBeRemoved = this.entries
            .filter(ent => ent.account.uuid === find.uuid);
        if (toBeRemoved.length == 0) {
            return this;
        }
        
        let builder = this.builder();
        for (let i = 0; i < toBeRemoved.length; i++) {
            const entry = toBeRemoved[i];
            const newEntry = new Entry(uuid(), replace.getRef(), entry.amount);
            builder = builder.removeEntry(entry).addEntry(newEntry);
        }
        return builder.build();
    }

    findReplaceCounterparty(find: Counterparty, replace: Counterparty) : Transaction {
        if (this.counterparty.uuid !== find.uuid) {
            return this;
        }

        return this.builder()
            .counterparty(replace.getRef())
            .build();
    }

    static identity() {
        return IdentityTransaction.self;
    }

    static fromJson(transaction:TransactionJSON) : Transaction {
        return new Transaction({
            uuid: transaction.uuid,
            date: new Date(Date.parse(transaction.date)),
            counterparty: Counterparty.createRef(transaction.counterparty),
            entries: transaction.entries.map(ent => Entry.fromJson(ent)),
            attachments: transaction.attachments ? transaction.attachments.map(uuid => ExternalFile.createRef(uuid)) : []
        });           
    }
}

class IdentityTransaction extends Transaction {
    private constructor() {
        super({
            uuid: "",
            date: new Date(0),
            counterparty: Counterparty.identityRef,
            entries : [],
            attachments: []
        });
    }
    static self = new IdentityTransaction();
}


class TransactionBuilder extends TrackingBuilder<Transaction> {
    private _uuid:string;
    private _date:Date;
    private _counterparty:Ref<Counterparty>;
    private _entries:Entry[];
    private _diff:TransactionDiff = {};
    private _description: string|undefined = undefined;
    private _transactionType?: Ref<TransactionType>;
    private _attachments:Ref<ExternalFile>[] = [];

    constructor(transaction?:Transaction) {
        super();
        const tx = transaction ? transaction : Transaction.identity();
        this._uuid = tx.uuid;
        this._date = tx.date;
        this._counterparty = tx.counterparty;
        this._entries = [...tx.entries];
        this._description = tx.description
        this._transactionType = tx.transactionType;
        this._attachments = [...tx.attachments];
    }

    uuid(uuid:string) {
        if (this.isTracking() && this._uuid !== uuid) {
            this._diff.uuid = uuid;
        }
        this._uuid = uuid;
        return this;
    }

    date(date:string|Date) {
        const parsedDate = (typeof date == "string") ? new Date(Date.parse(date)) : date;
        if (this.isTracking() && this._date.toISOString() !== parsedDate.toISOString()) {
            this._diff.date = parsedDate.toISOString()
        }
        this._date = parsedDate;
        return this;
    }

    description(description:string | undefined) {
        if (this.isTracking() && this._description !== description) {
            this._diff.description = description;
        }
        this._description = description;
        return this;
    }

    transactionType(ttype:string|Ref<TransactionType>) {
        const type = (typeof ttype === "string") ? TransactionType.createRef(ttype) : ttype; 
        if (this.isTracking() && (!this._transactionType || this._transactionType.uuid !== type.uuid)) {
            this._diff.transactionType = type.uuid;
        }
        this._transactionType = type;
        return this;
    }

    counterparty(counterparty:string|Ref<Counterparty>) {
        const cp = (typeof counterparty === "string") ? Counterparty.createRef(counterparty) : counterparty; 
        if (this.isTracking() && this._counterparty.uuid !== cp.uuid) {
            this._diff.counterparty = cp.uuid;
        }
        this._counterparty = cp;
        return this;
    }

    addEntry(entry:Entry) {
        const index = this._entries.findIndex(e => e.uuid === entry.uuid);
        if (this.isTracking() && index == -1) {
            this._diff.newEntries = this._diff.newEntries || [];
            this._diff.newEntries.push(entry.toJson());
        }
        if (index == -1) {
            this._entries.push(entry);
        }
        return this;
    }

    removeEntry(entry:Entry) {
        const index = this._entries.findIndex(e => e.uuid === entry.uuid);
        if (this.isTracking() && index >= 0) {
            this._diff.deletedEntries = this._diff.deletedEntries || [];
            this._diff.deletedEntries.push(entry.toJson());
        }
        if (index >= 0) {
            this._entries.splice(index, 1);
        }
        return this;
    }

    private removeEntriesNotIn(entries:Entry[]) {
        const uuidsToRetain = entries.map(ent => ent.uuid);

        const entriesToDelete = this._entries.filter(entry => uuidsToRetain.indexOf(entry.uuid) === -1);

        entriesToDelete.forEach(entry => {
            if (this.isTracking()) {
                this._diff.deletedEntries = this._diff.deletedEntries || [];
                this._diff.deletedEntries.push(entry.toJson());
            }
            const index = this._entries.indexOf(entry);
            this._entries.splice(index, 1);
        });
        return this;
    }

    addEntries(entries:Entry[]) {
        entries.forEach(this.addEntry.bind(this));
        return this;
    }

    removeEntries(entries:Entry[]) {
        entries.forEach(this.removeEntry.bind(this));
        return this;
    }

    addJSONEntries(entries:EntryJSON[]) {
        this.addEntries(entries.map(Entry.fromJson));
        return this;
    }

    removeJSONEntries(entries:EntryJSON[]) {
        this.removeEntries(entries.map(Entry.fromJson));
        return this;
    }

    addAttachement(attachement:Ref<ExternalFile>) {
        const index = this._attachments.findIndex(a => a.uuid === attachement.uuid);
        if (this.isTracking() && index == -1) {
            this._diff.newAttachements = this._diff.newAttachements || [];
            this._diff.newAttachements.push(attachement.uuid);
        }
        if (index == -1) {
            this._attachments.push(attachement);
        }
        return this;
    }
    
    removeAttachement(attachement:Ref<ExternalFile>) {
        const index = this._attachments.findIndex(a => a.uuid === attachement.uuid);
        if (this.isTracking() && index >= 0) {
            this._diff.deletedAttachments = this._diff.deletedAttachments || [];
            this._diff.deletedAttachments.push(attachement.uuid);
        }
        if (index >= 0) {
            this._attachments.splice(index, 1);
        }
        return this;
    }

    addAttachements(attachment:Ref<ExternalFile>[]) {
        attachment.forEach(this.addAttachement.bind(this));
        return this;
    }

    removeAttachements(attachments:Ref<ExternalFile>[]) {
        attachments.forEach(this.removeAttachement.bind(this));
        return this;
    }

    addJSONAttachements(attachments:string[]) {
        this.addAttachements(attachments.map(ExternalFile.createRef));
        return this;
    }

    removeJSONAttachements(attachments:string[]) {
        this.removeAttachements(attachments.map(ExternalFile.createRef));
        return this;
    }


    private removeAttachmentsNotIn(attachements:Ref<ExternalFile>[]) {
        const uuidsToRetain = attachements.map(ent => ent.uuid);

        const attsToDelete = this._attachments.filter(entry => uuidsToRetain.indexOf(entry.uuid) === -1);

        attsToDelete.forEach(attachment => {
            if (this.isTracking()) {
                this._diff.deletedAttachments = this._diff.deletedAttachments || [];
                this._diff.deletedAttachments.push(attachment.uuid);
            }
            const index = this._attachments.indexOf(attachment);
            this._attachments.splice(index, 1);
        });
        return this;
    }

    build(): Transaction {
        return new Transaction({
            uuid: this._uuid,
            date: this._date,
            counterparty: this._counterparty,
            entries: this._entries,
            transactionType: this._transactionType,
            description: this._description,
            attachments: this._attachments,
        });
    }

    applyDiff(diff: TransactionDiff): TransactionBuilder {
        if (diff.uuid) {
            this.uuid(diff.uuid);
        }
        if (diff.date) {
            this.date(diff.date);
        }
        if (diff.counterparty) {
            this.counterparty(diff.counterparty);
        }

        if (diff.description) {
            this.description(diff.description);
        }

        if (diff.transactionType) {
            this.transactionType(diff.transactionType);
        }

        if (diff.newEntries) {
            this.addJSONEntries(diff.newEntries);
        }
        if (diff.deletedEntries) {
            this.removeJSONEntries(diff.deletedEntries);
        }

        if (diff.newAttachements) {
            this.addJSONAttachements(diff.newAttachements);
        }
        if (diff.deletedAttachments) {
            this.removeJSONAttachements(diff.deletedAttachments);
        }
        return this;
    }

    applyObj(object: Transaction) : TransactionBuilder {
        this.uuid(object.uuid);
        this.date(object.date);
        this.counterparty(object.counterparty);
        this.description(object.description);
        if (object.transactionType) {
            this.transactionType(object.transactionType);    
        }
        this.removeEntriesNotIn(object.entries);
        this.addEntries(object.entries);

        this.removeAttachmentsNotIn(object.attachments);
        this.addAttachements(object.attachments);
        return this;
    }

    getDiff(): DiffContent<Transaction> {
        return this._diff;
    }

}

interface EntryJSON {
    uuid: string;
    account: string;
    amount: AmountJSON;
}

interface TransactionJSON {
    date: string;
    uuid: string;
    entries: EntryJSON[];
    counterparty: string;
    description?:string;
    transactionType?:string
    attachments?:string[];
}

interface TransactionObj {
    date: Date;
    uuid: string;
    entries: Entry[];
    counterparty: Ref<Counterparty>;
    transactionType?:Ref<TransactionType>;
    description?:string;
    attachments:Ref<ExternalFile>[];
}

interface TransactionDiff extends DiffContent<Transaction> {
    uuid?: string;
    date?: string;
    counterparty?: string;
    transactionType?:string;
    description?:string;
    newEntries?:EntryJSON[];
    deletedEntries?:EntryJSON[];
    deletedAttachments?:string[];
    newAttachements?:string[];
}

export {Transaction, Entry, TransactionBrief, TransactionJSON, EntryJSON, TransactionDiff, TransactionBuilder};